// « Photo Vivante » on a photo ALREADY in the album (owner, 2026-11-04).
//
// The team picks a printed photo and gives a video made from it (Google Flow
// and the like start the video on that exact photo). Those tools return 9:16
// or 16:9, so the video rarely has the photo's shape: they cut the photo to
// their shape first. The camera page lays the video over the WHOLE printed
// photo, so here, in the browser:
//   1. find where the video's first frame sits on the photo (findPlacement).
//      A video that does not start on it (a client's own edit) is fine too
//      once the team says « continuer quand même »: it then fills the photo;
//   2. send the ORIGINAL + that placement + the photo as the still. Our
//      server (deploy/living-prep, migration 290) makes the played video in
//      the photo's shape: the photo all around, the video in its place, then
//      after one second a smooth zoom until the video fills the photo
//      (videoRectAt — the same curve, in Python there). Nothing new prints.
// Guard: livingOnPhoto.test.ts
import type { LivingUploader } from './api';
import { LIVING_LONG_SIDE, originalOrLighter, put, type LivingStep } from './livingUpload';

/** The first frame is not the picked photo — the UI asks before going on. */
export class NotOnPhoto extends Error {
  constructor() { super('La vidéo ne commence pas sur cette photo.'); }
}
/** Above this, the first frame does not look like the photo. Japan p.2 Flow clip: 9.5. */
export const MATCH_MAX_SCORE = 22;

export interface Gray { data: ArrayLike<number>; w: number; h: number }
/** Where the video sits on the photo, in fractions of the photo (0–1). */
export interface Placement { x: number; y: number; w: number; h: number; score: number }

const at = (g: Gray, x: number, y: number) => {
  const xi = Math.min(g.w - 1, Math.max(0, Math.round(x))), yi = Math.min(g.h - 1, Math.max(0, Math.round(y)));
  return g.data[yi * g.w + xi];
};

/** Mean difference between the frame and the photo under `p`. Lower = better. Pure. */
export function placementScore(photo: Gray, frame: Gray, p: Omit<Placement, 'score'>, grid = 28): number {
  const gy = Math.max(4, Math.round(grid * (frame.h / frame.w)));
  let sum = 0;
  for (let j = 0; j < gy; j++) {
    for (let i = 0; i < grid; i++) {
      const u = (i + 0.5) / grid, v = (j + 0.5) / gy;
      const f = at(frame, u * frame.w - 0.5, v * frame.h - 0.5);
      const ph = at(photo, (p.x + u * p.w) * photo.w - 0.5, (p.y + v * p.h) * photo.h - 0.5);
      sum += Math.abs(f - ph);
    }
  }
  return sum / (grid * gy);
}

/**
 * Where the video frame sits on the photo. Starts from « the tool cut the
 * photo to its own shape, centred » and searches size and position around it
 * (AI tools zoom or shift a little). Pure.
 */
export function findPlacement(photo: Gray, frame: Gray): Placement {
  const pa = photo.w / photo.h, fa = frame.w / frame.h;
  // The centred cut of the photo in the video's shape, in photo fractions.
  const base = fa < pa ? { w: fa / pa, h: 1 } : { w: 1, h: pa / fa };
  let best: Placement = { x: (1 - base.w) / 2, y: (1 - base.h) / 2, ...base, score: Infinity };
  best.score = placementScore(photo, frame, best);
  const search = (scales: number[], shifts: number[], around: Placement) => {
    for (const s of scales) {
      const w = base.w * s, h = base.h * s;
      const cx = around.x + around.w / 2, cy = around.y + around.h / 2;
      for (const dx of shifts) for (const dy of shifts) {
        const p = { x: cx - w / 2 + dx, y: cy - h / 2 + dy, w, h };
        const score = placementScore(photo, frame, p);
        if (score < best.score) best = { ...p, score };
      }
    }
  };
  const start = { ...best };
  search([0.8, 0.85, 0.9, 0.95, 1, 1.05, 1.1, 1.15, 1.2], [-0.08, -0.06, -0.04, -0.02, 0, 0.02, 0.04, 0.06, 0.08], start);
  const coarse = { ...best }, s0 = coarse.w / base.w;
  search([-0.02, -0.01, 0, 0.01, 0.02].map((d) => s0 + d), [-0.012, -0.008, -0.004, 0, 0.004, 0.008, 0.012], coarse);
  return best;
}

/** Is the video already the photo, edge to edge (nothing to remake)? Pure. */
export function fillsPhoto(p: Placement, tol = 0.015): boolean {
  return Math.abs(p.x) < tol && Math.abs(p.y) < tol && Math.abs(p.w - 1) < tol * 2 && Math.abs(p.h - 1) < tol * 2;
}

export type Rect = { x: number; y: number; w: number; h: number };
/** The video sits exactly on the print this long, then grows to fill it. */
export const MATCH_HOLD_S = 1.0;
export const GROW_S = 1.2;

/**
 * Where to draw the video at time `t` in a `W × H` frame: `start` (lined up
 * with the print) until MATCH_HOLD_S, then a smooth zoom to the smallest
 * rectangle of the same shape that covers the whole frame. Pure.
 */
export function videoRectAt(t: number, start: Rect, W: number, H: number): Rect {
  const k = Math.max(1, W / start.w, H / start.h);
  const cw = start.w * k, ch = start.h * k;
  const cx = start.x + start.w / 2, cy = start.y + start.h / 2;
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  const end = { x: clamp(cx - cw / 2, W - cw, 0), y: clamp(cy - ch / 2, H - ch, 0), w: cw, h: ch };
  const u = clamp((t - MATCH_HOLD_S) / GROW_S, 0, 1);
  const p = u * u * (3 - 2 * u);   // smoothstep: starts and ends gently
  return { x: start.x + (end.x - start.x) * p, y: start.y + (end.y - start.y) * p, w: start.w + (end.w - start.w) * p, h: start.h + (end.h - start.h) * p };
}

/** Output size in the photo's shape, long side LIVING_LONG_SIDE, even numbers. Pure. */
export function photoShapeSize(pw: number, ph: number, max = LIVING_LONG_SIDE): { w: number; h: number } {
  const k = max / Math.max(pw, ph);
  const even = (n: number) => Math.max(2, Math.round((n * k) / 2) * 2);
  return { w: even(pw), h: even(ph) };
}

function grayOf(src: CanvasImageSource, w: number, h: number): Gray {
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(src, 0, 0, w, h);
  const px = g.getImageData(0, 0, w, h).data;
  const data = new Float32Array(w * h);
  for (let i = 0, j = 0; i < px.length; i += 4, j++) data[j] = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
  return { data, w, h };
}

const loadImage = (src: string) => new Promise<HTMLImageElement>((ok, ko) => {
  const i = new Image(); i.crossOrigin = 'anonymous';
  i.onload = () => ok(i); i.onerror = () => ko(new Error('La photo ne s’ouvre pas. Rechargez la page, puis réessayez.')); i.src = src;
});

async function firstFrame(file: File): Promise<{ video: HTMLVideoElement; url: string }> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true; video.playsInline = true; video.preload = 'auto'; video.src = url;
  await new Promise<void>((ok, ko) => {
    video.onloadeddata = () => ok();
    video.onerror = () => ko(new Error('Cette vidéo ne s’ouvre pas dans le navigateur (format). Envoyez-la en MP4.'));
  });
  if (video.currentTime !== 0) {
    await new Promise<void>((ok) => { video.onseeked = () => ok(); video.currentTime = 0; });
  }
  return { video, url };
}

/** The whole flow for a picked photo. Returns the album's link. Throws a
 *  French message, or NotOnPhoto unless `anyway` (the video then fills it). */
export async function addLivingVideoOnPhoto(
  file: File, albumIndex: number, photo: { id: string; url: string },
  api: LivingUploader, onStep: (s: LivingStep) => void, anyway = false,
): Promise<string> {
  if (!file.type.startsWith('video/') && !/\.(mp4|mov|webm)$/i.test(file.name)) throw new Error('Choisissez une vidéo.');
  onStep({ label: 'Lecture de la photo et de la vidéo…', pct: 3 });
  const img = await loadImage(photo.url);
  const { video, url } = await firstFrame(file);
  let placement: Placement;
  try {
    onStep({ label: 'Placement de la vidéo sur la photo…', pct: 10 });
    const pw = 160, ph = Math.max(8, Math.round(160 * (img.naturalHeight / img.naturalWidth)));
    const fw = 120, fh = Math.max(8, Math.round(120 * (video.videoHeight / video.videoWidth)));
    placement = findPlacement(grayOf(img, pw, ph), grayOf(video, fw, fh));
  } finally {
    URL.revokeObjectURL(url);
  }
  const onIt = placement.score <= MATCH_MAX_SCORE;
  if (!onIt && !anyway) throw new NotOnPhoto();

  // The still = the photo itself, as printed (long side ≤ 2400).
  const k = Math.min(1, 2400 / Math.max(img.naturalWidth, img.naturalHeight));
  const sc = document.createElement('canvas');
  sc.width = Math.round(img.naturalWidth * k); sc.height = Math.round(img.naturalHeight * k);
  sc.getContext('2d')!.drawImage(img, 0, 0, sc.width, sc.height);
  const still = await new Promise<Blob>((ok, ko) => sc.toBlob((b) => (b ? ok(b) : ko(new Error('photo'))), 'image/jpeg', 0.92));

  const { video: out, ext } = await originalOrLighter(file, onStep, 15, 60);
  onStep({ label: 'Envoi…', pct: 62 });
  const up = await api.upload(albumIndex, ext, photo.id);
  await put(up.upload.still, still, 'image/jpeg');
  onStep({ label: 'Envoi de la vidéo…', pct: 66 });
  const type = out === file ? (file.type || `video/${ext === 'mov' ? 'quicktime' : ext}`) : 'video/mp4';
  await put(up.upload.video, out, type, (p) => onStep({ label: 'Envoi de la vidéo…', pct: 66 + p * 30 }));
  onStep({ label: 'Presque fini…', pct: 97 });
  const { score: _score, ...rect } = placement;
  void _score;
  await api.ready(albumIndex, up.paths, 0, onIt && !fillsPhoto(placement) ? rect : null);
  return up.link;
}
